import { useCallback, useEffect, useMemo, useState } from 'react'
import { invalidateCachedFetch, useRealtimeSync } from '@structural-climbing/auth'

interface CheckInStatus {
  isCheckedIn: boolean
  checkedInAt: string | null
  childCheckIns: Record<string, string>
  refresh: () => Promise<void>
}

export function useMemberCheckInStatus(
  userId: string | undefined,
  gymId: string | null,
  apiUrl: string,
  getToken: () => Promise<string>,
): CheckInStatus {
  const [isCheckedIn, setIsCheckedIn] = useState(false)
  const [checkedInAt, setCheckedInAt] = useState<string | null>(null)
  const [childCheckIns, setChildCheckIns] = useState<Record<string, string>>({})

  const refresh = useCallback(async () => {
    if (!userId || !gymId) return
    try {
      const token = await getToken()
      const url = gymId
        ? `${apiUrl}/my-check-in-status?gym_id=${encodeURIComponent(gymId)}`
        : `${apiUrl}/my-check-in-status`
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        const data = (await res.json()) as {
          is_checked_in: boolean
          checked_in_at?: string
          children: Record<string, string>
        }
        setIsCheckedIn(data.is_checked_in)
        setCheckedInAt(data.checked_in_at ?? null)
        setChildCheckIns(data.children ?? {})
      }
    } catch {
      // non-critical
    }
  }, [userId, gymId, apiUrl, getToken])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const bindings = useMemo(
    () => (userId ? [{ table: 'check_ins', filter: `user_id=eq.${userId}` }] : []),
    [userId],
  )

  // Realtime is the fast path, not the only one. A dropped socket, a sleeping
  // phone or a tab that was in the background all end with the member looking
  // at a screen that still says they are outside the gym, seconds after the
  // desk scanned them in. useRealtimeSync re-asks on foreground and while the
  // socket is down.
  const onCheckInChange = useCallback(() => {
    void refresh()
    // A check-in moves the gym's live headcount and the is_checked_in flag on
    // the membership list, and it burns an entry off the plan the hero shows —
    // none of which is fetched here.
    invalidateCachedFetch('gym-occupancy:')
    invalidateCachedFetch('my-gyms')
    invalidateCachedFetch('my-subscription:')
  }, [refresh])

  useRealtimeSync({
    channel: userId ? `member-checkins-${userId}` : null,
    bindings,
    onChange: onCheckInChange,
    onResync: onCheckInChange,
  })

  return { isCheckedIn, checkedInAt, childCheckIns, refresh }
}
